import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import {
  X, Sparkles, Loader2, AlertCircle, CalendarDays, Paperclip,
  Lock, LockOpen, Trash2, MapPin, Receipt, RefreshCw, Calendar,
  Flag, Clock, Tag, Users, Tags, Bell, CircleDot, Command, SquarePen, Plus
} from 'lucide-react';

import { projectsApi, labelsApi, tasksApi, aiApi, taskTypesApi, teamsApi, attachmentsApi, trashApi, botsApi } from '../../services/api';
import { useScopeBots } from '../bots/botDisplay';
import { useTask } from '../../contexts/TaskContext';
import { useAuth } from '../../contexts/AuthContext';
import { useUndo } from '../../contexts/UndoContext';
import { useDateTime } from '../../contexts/DateTimeContext';

import BlockNoteEditor from '../LazyBlockNoteEditor';
import ProjectPicker from '../ui/ProjectPicker';
import AttachmentPreviewModal from '../ui/AttachmentPreviewModal';
import SeriesUpdateDialog from '../SeriesUpdateDialog';
import { Switch } from '../ui/switch';

import DatePicker from './pickers/DatePicker';
import PriorityPicker from './pickers/PriorityPicker';
import DurationPicker from './pickers/DurationPicker';
import StatusPicker from './pickers/StatusPicker';
import LabelPicker from './pickers/LabelPicker';
import AssigneePicker from './pickers/AssigneePicker';
import TaskTypePicker from './pickers/TaskTypePicker';
import ReminderPicker from './pickers/ReminderPicker';
import RecurrencePicker from './pickers/RecurrencePicker';
import CompactPropertyRow from './CompactPropertyRow';
import AttachmentUploadButton from './AttachmentUploadButton';
import { descriptionToDoc, textToDoc } from '../../lib/constants';

const UnifiedTaskCreation = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { pushUndo } = useUndo();
  const { formatDate: formatDateContext, formatTime: formatTimeContext, formatDayOfWeek, toUTCISO, toLocalDateString } = useDateTime();
  const { isCreationOpen, creationPrefills, closeTaskCreation, notifyTaskMutated } = useTask();

  // Derived editing state from prefills
  const editingTask = creationPrefills?.editingTask || null;

  // Form state
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState(null);
  const [dueDate, setDueDate] = useState(null);
  const [dueTime, setDueTime] = useState('');
  const [priority, setPriority] = useState('p4');
  const [projectId, setProjectId] = useState('');
  const [status, setStatus] = useState('todo');
  const [statusId, setStatusId] = useState(null);
  const [labelIds, setLabelIds] = useState([]);
  const [location, setLocation] = useState('');
  const [reminders, setReminders] = useState([]);
  const [duration, setDuration] = useState(60);
  const [scheduledStart, setScheduledStart] = useState(null);
  const [scheduledEnd, setScheduledEnd] = useState(null);
  const [taskTypeId, setTaskTypeId] = useState(null);
  const [needsInvoice, setNeedsInvoice] = useState(null);
  const [assignedTo, setAssignedTo] = useState([]);
  const [botId, setBotId] = useState(null);
  const [isLocked, setIsLocked] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [previewAttachment, setPreviewAttachment] = useState(null);
  const [recurrence, setRecurrence] = useState(null);

  // AI state
  const [aiMode, setAiMode] = useState(false);
  const [aiParsing, setAiParsing] = useState(false);

  // Data
  const [projects, setProjects] = useState([]);
  // Bots of the chosen project's team (or the creator's personal bots).
  const creationTeamId = projects.find((p) => p.id === projectId)?.team_id || null;
  const creationBots = useScopeBots(creationTeamId, editingTask?.user_id || user?.id);
  const [labels, setLabels] = useState([]);
  const [taskTypes, setTaskTypes] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [seriesDialogPayload, setSeriesDialogPayload] = useState(null);
  const [seriesPendingMeta, setSeriesPendingMeta] = useState(null);
  const [projectTeamId, setProjectTeamId] = useState(null);

  const titleRef = useRef(null);
  const submittingRef = useRef(false); // synchronous guard against double submit

  // ------------------------------------------------------------------
  // Reset form
  // ------------------------------------------------------------------
  const resetForm = useCallback(() => {
    setTitle('');
    setDescription(null);
    setDueDate(null);
    setDueTime('');
    setPriority('p4');
    setProjectId('');
    setStatus('todo');
    setStatusId(null);
    setLabelIds([]);
    setLocation('');
    setReminders([]);
    setDuration(60);
    setScheduledStart(null);
    setScheduledEnd(null);
    setTaskTypeId(null);
    setNeedsInvoice(null);
    setAssignedTo(user?.id ? [user.id] : []);
    setBotId(null);
    setIsLocked(false);
    setTeamMembers([]);
    setRecurrence(null);
    setAttachments([]);
    setError(null);
    setAiMode(false);
  }, [user?.id]);

  // ------------------------------------------------------------------
  // Load reference data on open
  // ------------------------------------------------------------------
  const loadData = useCallback(async () => {
    try {
      const [projectsRes, labelsRes, taskTypesRes] = await Promise.all([
        projectsApi.getAll(),
        labelsApi.getAll(),
        taskTypesApi.getAll(),
      ]);
      setProjects(projectsRes.data || []);
      setLabels(labelsRes.data || []);
      setTaskTypes(taskTypesRes.data || []);

      // Set default task type for new tasks
      if (!editingTask && taskTypesRes.data?.length > 0) {
        const defaultType = taskTypesRes.data.find(t => t.name === 'Task') || taskTypesRes.data[0];
        setTaskTypeId(defaultType.id);
        setNeedsInvoice(defaultType.needs_invoice_default);
      }
    } catch (err) {
      console.error('Error loading data:', err);
    }
  }, [editingTask]);

  // ------------------------------------------------------------------
  // Open / populate: once each time the modal opens. Through a ref, so prefills or the
  // user object changing while the person types never fills the form again.
  // ------------------------------------------------------------------
  const populateRef = useRef(null);
  populateRef.current = () => {
    loadData();
    setTimeout(() => titleRef.current?.focus(), 100);

    const pf = creationPrefills || {};

    if (pf.editingTask) {
      const task = pf.editingTask;
      setTitle(task.title || '');

      setDescription(descriptionToDoc(task.description));

      setDueDate(task.due_date ? new Date(task.due_date) : null);
      setDueTime(task.due_time || '');
      setPriority(task.priority || 'p4');
      setProjectId(task.project_id || '');
      setStatus(task.status || 'todo');
      setStatusId(task.status_id || null);
      setLabelIds(task.label_ids || []);
      setLocation(task.location || '');
      setReminders(task.reminders || []);
      setDuration(task.duration || 60);
      setTaskTypeId(task.task_type_id || null);
      setNeedsInvoice(task.needs_invoice ?? null);
      setAttachments(task.attachments || []);
      const at = task.assigned_to;
      setAssignedTo(Array.isArray(at) ? at : at ? [at] : []);
      setBotId(task.assigned_bot_id || null);
      setIsLocked(task.is_locked ?? false);
      if (task.recurrence) {
        setRecurrence(task.recurrence);
      } else {
        setRecurrence(null);
      }
      if (task.scheduled_start) setScheduledStart(new Date(task.scheduled_start));
      if (task.scheduled_end) setScheduledEnd(new Date(task.scheduled_end));
    } else {
      resetForm();

      // Apply prefills for new tasks
      if (pf.date) setDueDate(new Date(pf.date));
      if (pf.startTime) {
        setDueTime(pf.startTime);
        if (pf.date) {
          const start = new Date(pf.date);
          const [hours, minutes] = pf.startTime.split(':');
          start.setHours(parseInt(hours), parseInt(minutes), 0, 0);
          setScheduledStart(start);
        }
      }
      if (pf.endTime && pf.date) {
        const end = new Date(pf.date);
        const [hours, minutes] = pf.endTime.split(':');
        end.setHours(parseInt(hours), parseInt(minutes), 0, 0);
        setScheduledEnd(end);
      }
      if (pf.scheduledStartISO) setScheduledStart(new Date(pf.scheduledStartISO));
      if (pf.scheduledEndISO) setScheduledEnd(new Date(pf.scheduledEndISO));
      if (pf.duration) setDuration(pf.duration);
      if (pf.projectId) setProjectId(pf.projectId);
      // Text, photos and files shared into the app from another app (src/native/intents.js)
      if (pf.title) setTitle(pf.title);
      if (pf.description) {
        setDescription(textToDoc(pf.description));
      }
      if (pf.files?.length) uploadFiles(pf.files);
      if (pf.status) setStatus(pf.status);
      if (pf.statusId) setStatusId(pf.statusId);

      // New tasks: pre-assign current user
      setAssignedTo(user?.id ? [user.id] : []);
      setIsLocked(false);
    }
  };
  useEffect(() => {
    if (isCreationOpen) populateRef.current();
  }, [isCreationOpen]);

  // ------------------------------------------------------------------
  // Load team members when project changes
  // ------------------------------------------------------------------
  useEffect(() => {
    if (!projectId || !projects.length) {
      setTeamMembers([]);
      setProjectTeamId(null);
      return;
    }
    const project = projects.find(p => p.id === projectId);
    if (project?.team_id) {
      setProjectTeamId(project.team_id);
      teamsApi.getById(project.team_id)
        .then(res => setTeamMembers(res.data?.members || []))
        .catch(() => setTeamMembers([]));
    } else {
      setProjectTeamId(null);
      setTeamMembers([]);
    }
  }, [projectId, projects]);

  // ------------------------------------------------------------------
  // Close handler
  // ------------------------------------------------------------------
  const handleClose = useCallback(() => {
    closeTaskCreation();
    resetForm();
  }, [closeTaskCreation, resetForm]);

  // ------------------------------------------------------------------
  // Global keyboard handler (submits through a ref: handleSubmit reads the whole form)
  // ------------------------------------------------------------------
  const submitRef = useRef(null);
  useEffect(() => {
    if (!isCreationOpen) return;
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
        e.preventDefault();
        e.stopPropagation();
        if (title.trim() && !saving) submitRef.current();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isCreationOpen, title, saving]);

  // ------------------------------------------------------------------
  // AI Parse
  // ------------------------------------------------------------------
  const handleAiParse = async () => {
    if (!title.trim()) return;
    setAiParsing(true);
    setError(null);
    try {
      const response = await aiApi.parse(title);
      const parsed = response.data?.parsed || response.data;

      if (!parsed || !parsed.title) {
        setError(t('tasks.ai_parse_invalid'));
        return;
      }

      if (parsed.title) setTitle(parsed.title);
      if (parsed.due_date) setDueDate(new Date(parsed.due_date));
      if (parsed.due_time) setDueTime(parsed.due_time);
      if (parsed.priority) setPriority(parsed.priority);
      if (parsed.duration) setDuration(parsed.duration);

      if (parsed.description) {
        setDescription(textToDoc(parsed.description));
      }
      if (parsed.location) setLocation(parsed.location);

      // Find project by name
      if (parsed.project_name) {
        const project = projects.find(p => p.name.toLowerCase().includes(parsed.project_name.toLowerCase()));
        if (project) setProjectId(project.id);
      }

      // Find labels by name
      if (parsed.labels?.length > 0) {
        const foundLabels = labels.filter(l =>
          parsed.labels.some(pl => l.name.toLowerCase().includes(pl.toLowerCase()))
        );
        setLabelIds(foundLabels.map(l => l.id));
      }

      // Match task type by name
      if (parsed.task_type && taskTypes.length > 0) {
        const typeMapping = { task: 'Task', feature: 'Feature', bug: 'Bug', maintenance: 'Maintenance', admin: 'Admin' };
        const typeName = typeMapping[parsed.task_type] || parsed.task_type;
        const matchedType = taskTypes.find(t => t.name.toLowerCase() === typeName.toLowerCase());
        if (matchedType) {
          setTaskTypeId(matchedType.id);
          if (parsed.billable === null || parsed.billable === undefined) {
            setNeedsInvoice(matchedType.needs_invoice_default);
          }
        }
      }

      if (parsed.billable !== null && parsed.billable !== undefined) {
        setNeedsInvoice(parsed.billable);
      }

      // BG_64: the AI button used to apply the parsed result silently. For a
      // plain title with no natural-language markers (date, time, priority,
      // #tag, @project, ...) the parse returns essentially the same content,
      // so nothing visibly changed and the button appeared to do nothing.
      // Give explicit feedback in both cases: a success toast when details
      // were actually extracted, otherwise an informational toast explaining
      // that nothing was detected and how to phrase the input.
      const titleChanged = parsed.title && parsed.title.trim() !== title.trim();
      const foundDetails =
        titleChanged ||
        parsed.due_date ||
        parsed.due_time ||
        (parsed.labels?.length > 0) ||
        parsed.project_name ||
        parsed.duration ||
        parsed.location ||
        parsed.description ||
        parsed.recurrence ||
        parsed.assignee_email;

      if (foundDetails) {
        toast.success(t('tasks.ai_parse_success'));
      } else {
        toast(t('tasks.ai_parse_no_details'));
      }

      setAiMode(false);
    } catch (err) {
      console.error('AI parsing error:', err);
      setError(t('tasks.ai_parse_failed'));
    } finally {
      setAiParsing(false);
    }
  };

  // ------------------------------------------------------------------
  // Submit
  // ------------------------------------------------------------------
  const handleSubmit = async () => {
    if (!title.trim()) return;
    // Guard against double submission. Ctrl+Enter is handled by both a global
    // keydown listener and the focused field's onKeyDown, which both fire in
    // the same tick — before `saving` state flips — so the `saving` check alone
    // let the task be created twice. A synchronous ref blocks the second call.
    if (submittingRef.current) return;
    submittingRef.current = true;
    setSaving(true);
    setError(null);

    const pf = creationPrefills || {};

    try {
      let finalScheduledStartISO = null;
      let finalScheduledEndISO = null;

      if (pf.scheduledStartISO && !editingTask) {
        finalScheduledStartISO = pf.scheduledStartISO;
        finalScheduledEndISO = pf.scheduledEndISO;
      } else if (scheduledStart) {
        finalScheduledStartISO = toUTCISO(scheduledStart);
        finalScheduledEndISO = scheduledEnd ? toUTCISO(scheduledEnd) : null;
      } else if (dueDate && dueTime) {
        const start = new Date(dueDate);
        const [hours, minutes] = dueTime.split(':');
        start.setHours(parseInt(hours), parseInt(minutes), 0, 0);
        const end = new Date(start.getTime() + duration * 60 * 1000);
        finalScheduledStartISO = toUTCISO(start);
        finalScheduledEndISO = toUTCISO(end);
      }

      // Guard: block scheduling in the past
      if (finalScheduledStartISO && new Date(finalScheduledStartISO) < new Date()) {
        toast.error(t('tasks.schedule_in_past_error'));
        setSaving(false);
        return;
      }

      const taskData = {
        title: title.trim(),
        description: description || null,
        due_date: toLocalDateString(dueDate),
        due_time: dueTime || null,
        priority,
        status,
        // status_id is what the Kanban board (and any other group-by-status
        // view) reads to place the task in the right column. Was previously
        // missing from this payload, so tasks created via "+ Add" inside a
        // column landed under the default column and looked like they had
        // disappeared.
        status_id: statusId || null,
        project_id: projectId || null,
        label_ids: labelIds,
        location: location || null,
        reminders,
        duration,
        scheduled_start: finalScheduledStartISO,
        scheduled_end: finalScheduledEndISO,
        task_type_id: taskTypeId || null,
        needs_invoice: needsInvoice,
        assigned_to: assignedTo.length > 0 ? assignedTo : null,
        team_id: pf.teamId || projectTeamId || null,
        recurrence: recurrence || null,
        attachments,
        is_locked: isLocked,
        parent_id: pf.parentTaskId || null,
        section_id: pf.sectionId || null,
      };

      if (editingTask) {
        const originalData = {
          title: editingTask.title,
          description: editingTask.description,
          due_date: editingTask.due_date,
          due_time: editingTask.due_time,
          priority: editingTask.priority,
          status: editingTask.status,
          project_id: editingTask.project_id,
          label_ids: editingTask.label_ids,
          location: editingTask.location,
          reminders: editingTask.reminders,
          duration: editingTask.duration,
          scheduled_start: editingTask.scheduled_start,
          scheduled_end: editingTask.scheduled_end,
          task_type_id: editingTask.task_type_id,
          needs_invoice: editingTask.needs_invoice,
          assigned_to: editingTask.assigned_to,
          recurrence: editingTask.recurrence,
          attachments: editingTask.attachments,
          is_locked: editingTask.is_locked,
        };
        const taskId = editingTask.id;
        const taskTitle = editingTask.title;

        // If editing a recurring task with a series, ask user
        if (editingTask.recurrence && editingTask.series_id) {
          setSeriesDialogPayload(taskData);
          setSeriesPendingMeta({ taskId, taskTitle, originalData });
          setSaving(false);
          return;
        }

        await tasksApi.update(taskId, taskData);
        if ((botId || null) !== (editingTask.assigned_bot_id || null)) {
          await botsApi.assignTask(taskId, botId).catch(() => {});
        }
        notifyTaskMutated();
        handleClose();
        pushUndo({
          label: t('tasks.task_edited', { name: taskTitle }),
          undo: async () => {
            await tasksApi.update(taskId, originalData);
            // axios interceptor invalidates the tasks query
          },
        });
      } else {
        const res = await tasksApi.create(taskData);
        // Handing the new task to a bot is a separate call (bots are not part of
        // the task payload); the interceptor refreshes lists either way.
        if (botId && res?.data?.id) {
          await botsApi.assignTask(res.data.id, botId).catch(() => {});
        }
        // axios interceptor invalidates the tasks query
        notifyTaskMutated();
        handleClose();
      }
    } catch (err) {
      console.error('Error saving task:', err);
      if (err.response?.status !== 402) {
        setError(err.response?.data?.detail || t('tasks.task_save_failed'));
      }
    } finally {
      setSaving(false);
      submittingRef.current = false;
    }
  };
  submitRef.current = handleSubmit;

  // ------------------------------------------------------------------
  // Series update handlers
  // ------------------------------------------------------------------
  const handleSeriesJustThis = async () => {
    const { taskId, taskTitle, originalData } = seriesPendingMeta;
    try {
      await tasksApi.update(taskId, { ...seriesDialogPayload, update_series: false });
      setSeriesDialogPayload(null);
      setSeriesPendingMeta(null);
      notifyTaskMutated();
      handleClose();
      pushUndo({
        label: t('tasks.task_edited', { name: taskTitle }),
        undo: async () => {
          await tasksApi.update(taskId, originalData);
          // axios interceptor invalidates the tasks query
        },
      });
    } catch {
      toast.error(t('tasks.task_save_failed'));
    }
  };

  const handleSeriesAllFuture = async () => {
    const { taskId, taskTitle, originalData } = seriesPendingMeta;
    try {
      await tasksApi.update(taskId, { ...seriesDialogPayload, update_series: true });
      setSeriesDialogPayload(null);
      setSeriesPendingMeta(null);
      notifyTaskMutated();
      handleClose();
      pushUndo({
        label: t('tasks.task_edited_series', { name: taskTitle }),
        undo: async () => {
          await tasksApi.update(taskId, originalData);
          // axios interceptor invalidates the tasks query
        },
      });
    } catch {
      toast.error(t('tasks.task_save_failed'));
    }
  };

  // ------------------------------------------------------------------
  // Move to trash (edit mode)
  // ------------------------------------------------------------------
  const handleMoveToTrash = async () => {
    if (!editingTask?.id) return;
    const taskId = editingTask.id;
    const taskTitle = editingTask.title || t('tasks.untitled_task');
    try {
      await tasksApi.delete(taskId);
      handleClose();
      notifyTaskMutated();
      pushUndo({
        label: t('tasks.task_moved_to_trash', { name: taskTitle }),
        undo: async () => {
          await trashApi.restoreTask(taskId);
          // axios interceptor invalidates the tasks query
          // axios interceptor invalidates the trash query
        },
      });
    } catch {
      toast.error(t('tasks.task_trash_failed'));
    }
  };

  // ------------------------------------------------------------------
  // File attachments
  // ------------------------------------------------------------------
  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    e.target.value = '';
    await uploadFiles(files);
  };

  async function uploadFiles(files) {
    setUploadingFile(true);
    try {
      // Parallel uploads; partial success is fine. `allSettled` lets one
      // oversize file fail without losing the rest.
      const results = await Promise.allSettled(files.map((f) => attachmentsApi.upload(f)));
      const uploaded = [];
      const failed = [];
      const overLimit = [];
      const tooLarge = []; // {name, limitMB}
      results.forEach((r, i) => {
        if (r.status === 'fulfilled' && r.value?.data) {
          uploaded.push(r.value.data);
        } else if (r.reason?.response?.status === 402) {
          overLimit.push(files[i].name);
        } else if (r.reason?.response?.status === 413) {
          const limitBytes = r.reason.response.data?.detail?.limit_bytes;
          const limitMB = limitBytes ? Math.floor(limitBytes / (1024 * 1024)) : null;
          tooLarge.push({ name: files[i].name, limitMB });
        } else {
          failed.push(files[i].name);
        }
      });
      if (uploaded.length) {
        setAttachments(prev => [...prev, ...uploaded]);
      }
      if (overLimit.length) toast.error(t('tasks.storage_limit_reached'));
      if (tooLarge.length) {
        // Inline error to match the rest of this creation form (toasts can be
        // missed when the user is looking at the modal).
        setError(
          tooLarge
            .map(({ name, limitMB }) => `${name} ${t('tasks.attachment_too_large', { limit: limitMB ?? '?' })}`)
            .join(' · ')
        );
      }
      if (failed.length) setError(`${t('tasks.file_upload_failed')}: ${failed.join(', ')}`);
    } catch (err) {
      console.error('Upload failed:', err);
      setError(t('tasks.file_upload_failed'));
    } finally {
      setUploadingFile(false);
    }
  }

  const handleRemoveAttachment = async (att) => {
    setAttachments(prev => prev.filter(a => a.id !== att.id));
    try {
      await attachmentsApi.delete(att.id, att.object_key);
    } catch (err) {
      console.error('Delete failed:', err);
    }
  };

  // ------------------------------------------------------------------
  // Label CRUD
  // ------------------------------------------------------------------
  const handleCreateLabel = async ({ name, color }) => {
    try {
      const res = await labelsApi.create({ name, color });
      const created = res.data;
      setLabels(prev => [...prev, created]);
      window.dispatchEvent(new CustomEvent('labelsUpdated'));
      // BG_74: acknowledge the new label with a proper success toast. Previously
      // the creation form gave no feedback at all (and the detail view showed a
      // misleading "Labels changed" undo toast from the auto-assignment).
      toast.success(t('tasks.label_created'));
      return created;
    } catch (err) {
      console.error('Failed to create label:', err);
      return null;
    }
  };

  const handleDeleteLabel = async (labelId) => {
    try {
      await labelsApi.delete(labelId);
      setLabels(prev => prev.filter(l => l.id !== labelId));
      setLabelIds(prev => prev.filter(id => id !== labelId));
    } catch (err) {
      console.error('Failed to delete label:', err);
    }
  };

  const handleUpdateLabel = async (label, patch) => {
    try {
      const res = await labelsApi.update(label.id, { name: label.name, color: label.color, ...patch });
      setLabels(prev => prev.map(l => l.id === label.id ? res.data : l));
      window.dispatchEvent(new CustomEvent('labelsUpdated'));
    } catch (err) {
      console.error('Failed to update label:', err);
    }
  };

  // ------------------------------------------------------------------
  // Scheduled info banner data
  // ------------------------------------------------------------------
  const pf = creationPrefills || {};
  const bannerStart = pf.scheduledStartISO ? new Date(pf.scheduledStartISO) : scheduledStart;
  const bannerEnd = pf.scheduledEndISO ? new Date(pf.scheduledEndISO) : scheduledEnd;

  // ------------------------------------------------------------------
  // Render
  // ------------------------------------------------------------------
  return (
    <>
      <DialogPrimitive.Root open={isCreationOpen} onOpenChange={(open) => { if (!open) handleClose(); }}>
        <DialogPrimitive.Portal>
          <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm" />
          <DialogPrimitive.Content
            className="fixed inset-0 z-50 flex items-center justify-center p-4 max-md:p-0 max-md:bottom-auto max-md:h-[var(--vvh,100dvh)]"
            onOpenAutoFocus={(e) => e.preventDefault()}
            onEscapeKeyDown={(e) => { if (previewAttachment) e.preventDefault(); }}
            onInteractOutside={(e) => { if (previewAttachment) e.preventDefault(); }}
          >
            <DialogPrimitive.Title className="sr-only">
              {editingTask ? t('tasks.edit_task', 'Edit task') : t('tasks.new_task', 'New task')}
            </DialogPrimitive.Title>
            <div
              className="w-[80vw] h-[90vh] max-w-[1400px] bg-white dark:bg-popover rounded-2xl border border-border dark:border-border shadow-2xl flex flex-col overflow-hidden max-md:w-full max-md:h-[var(--vvh,100dvh)] max-md:max-w-none max-md:rounded-none max-md:border-0 max-md:pt-[env(safe-area-inset-top)] max-md:pb-[env(safe-area-inset-bottom)]"
              onKeyDown={(e) => {
                if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
                  e.preventDefault();
                  e.stopPropagation();
                  if (title.trim() && !saving) handleSubmit();
                }
              }}
            >
              {/* ============ HEADER ============ */}
              <div className="flex items-center gap-3 px-5 py-3 border-b border-border dark:border-border flex-shrink-0">
                <SquarePen className="w-5 h-5 text-amber-700 dark:text-amber-400" />
                <span className="text-muted-foreground text-sm font-medium">
                  {editingTask ? t('tasks.edit_task') : t('tasks.new_task')}
                </span>
                <div className="ml-auto flex items-center gap-2">
                  <kbd className="hidden sm:inline-flex items-center gap-0.5 px-2 py-1 text-[10px] bg-muted dark:bg-muted rounded text-muted-foreground border border-border dark:border-border">
                    <Command className="w-3 h-3" />K
                  </kbd>
                  <button
                    onClick={handleClose}
                    className="p-1.5 hover:bg-accent rounded-lg transition-colors"
                  >
                    <X className="w-4 h-4 text-muted-foreground" />
                  </button>
                </div>
              </div>

              {/* ============ BODY ============ */}
              {/* Desktop: two columns side by side. Mobile: single scrollable column */}
              <div className="flex-1 flex min-h-0 overflow-hidden max-md:flex-col max-md:overflow-y-auto">

                {/* ---- LEFT / TOP: Title + Description ---- */}
                <div className="flex-1 flex flex-col min-h-0 overflow-y-auto border-r border-border dark:border-border max-md:border-r-0 max-md:overflow-visible max-md:flex-none">
                  {/* Scheduled Time Info Banner */}
                  {bannerStart && bannerEnd && (
                    <div className="mx-4 mt-3 p-3 bg-amber-500/10 rounded-lg border border-amber-500/20">
                      <div className="flex items-center gap-2 text-amber-700 dark:text-amber-400 text-sm">
                        <CalendarDays className="w-4 h-4" />
                        <span className="font-medium">
                          {formatDayOfWeek(bannerStart)} {formatDateContext(bannerStart)}
                        </span>
                      </div>
                      <div className="text-foreground font-medium mt-1">
                        {formatTimeContext(bannerStart)} - {formatTimeContext(bannerEnd)}
                        <span className="text-muted-foreground ml-2">
                          ({t('ui_task.duration.minutes', { m: Math.round((new Date(bannerEnd) - new Date(bannerStart)) / (1000 * 60)) })})
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Title Input */}
                  <div className="p-4 pb-2">
                    <div className="flex items-center gap-2 bg-muted dark:bg-white/5 rounded-lg border border-border dark:border-border focus-within:border-amber-500/50 transition-colors p-1.5">
                      <input
                        ref={titleRef}
                        type="text"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        placeholder={t('tasks.task_input_placeholder')}
                        className="flex-1 bg-transparent text-foreground dark:text-foreground text-sm placeholder:text-muted-foreground dark:placeholder:text-muted-foreground focus:outline-none px-2.5 py-1.5"
                        autoComplete="off"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault();
                            if (aiMode) handleAiParse();
                            else if (title.trim() && !saving) handleSubmit();
                          }
                        }}
                      />
                      {/* AI Parse Button */}
                      <button
                        onClick={handleAiParse}
                        disabled={!title.trim() || aiParsing}
                        className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium bg-gradient-to-r from-purple-500/20 to-blue-500/20 hover:from-purple-500/30 hover:to-blue-500/30 text-purple-300 rounded-md transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                        title={t('tasks.task_input_placeholder')}
                      >
                        {aiParsing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                        <span className="hidden sm:inline">AI</span>
                      </button>
                    </div>
                  </div>

                  {/* Description Editor */}
                  <div className="px-4 pb-4 flex-1 min-h-0">
                    <div className="bg-accent/60 rounded-lg border border-border p-3 h-full">
                      <BlockNoteEditor
                        content={description}
                        onChange={setDescription}
                        placeholder={t('tasks.description_placeholder')}
                        minHeight="80px"
                        className="text-sm"
                        enableImageUpload={true}
                        onImageUpload={(att) => setAttachments(prev => [...prev, att])}
                      />
                    </div>
                  </div>

                  {/* Attachments List (left column, below description) */}
                  {attachments.length > 0 && (
                    <div className="px-4 pb-3 space-y-1">
                      {attachments.map(att => (
                        <div key={att.id} className="group flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-accent/60">
                          {att.content_type?.startsWith('image/') ? (
                            <img src={att.url} alt={att.filename} className="w-8 h-8 rounded object-cover flex-shrink-0" />
                          ) : (
                            <Paperclip className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                          )}
                          <button
                            onClick={() => setPreviewAttachment(att)}
                            className="flex-1 text-sm text-foreground/80 hover:text-foreground truncate text-left"
                          >
                            {att.filename}
                          </button>
                          <span className="text-xs text-muted-foreground/70">{t('ui_task.attachments.size_kb', { size: Math.round(att.size / 1024) })}</span>
                          <button
                            onClick={() => handleRemoveAttachment(att)}
                            // Always visible: the only control to remove an attachment,
                            // hidden on touch devices when revealed on hover only.
                            className="p-0.5 text-muted-foreground hover:text-red-400 transition-colors flex-shrink-0"
                          >
                            <X className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* ---- RIGHT / BOTTOM: Properties ---- */}
                <div className="w-[300px] flex-shrink-0 bg-muted dark:bg-card overflow-y-auto max-md:w-full max-md:flex-none max-md:border-t max-md:border-border dark:max-md:border-border">

                  {/* --- CORE --- */}
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground/70 px-3 pt-3 pb-1">
                    {t('tasks.core', 'Core')}
                  </div>

                  <CompactPropertyRow icon={CircleDot} label={t('tasks.status_label', 'Status')}>
                    <StatusPicker
                      value={status}
                      onChange={setStatus}
                      customStatuses={[]}
                      statusId={statusId}
                      onStatusIdChange={setStatusId}
                    />
                  </CompactPropertyRow>

                  <CompactPropertyRow icon={Flag} label={t('tasks.priority', 'Priority')}>
                    <PriorityPicker value={priority} onChange={setPriority} />
                  </CompactPropertyRow>

                  <CompactPropertyRow icon={Calendar} label={t('tasks.due_date', 'Due Date')}>
                    <DatePicker
                      value={dueDate}
                      onChange={setDueDate}
                      timeValue={dueTime}
                      onTimeChange={setDueTime}
                    />
                  </CompactPropertyRow>

                  <CompactPropertyRow icon={Clock} label={t('tasks.duration', 'Duration')}>
                    <DurationPicker value={duration} onChange={setDuration} />
                  </CompactPropertyRow>

                  {/* --- DETAILS --- */}
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground/70 px-3 pt-3 pb-1">
                    {t('tasks.details', 'Details')}
                  </div>

                  <CompactPropertyRow icon={Tags} label={t('tasks.project', 'Project')}>
                    <ProjectPicker
                      selectedProjectId={projectId}
                      onSelect={(id) => setProjectId(id || '')}
                      projects={projects}
                      onProjectsChange={setProjects}
                    />
                  </CompactPropertyRow>

                  <CompactPropertyRow icon={Users} label={t('tasks.assignee', 'Assignee')}>
                    <AssigneePicker
                      value={assignedTo}
                      teamMembers={teamMembers}
                      onChange={setAssignedTo}
                      bots={creationBots}
                      botId={botId}
                      onBotChange={setBotId}
                    />
                  </CompactPropertyRow>

                  <CompactPropertyRow icon={Tag} label={t('tasks.labels', 'Labels')}>
                    <LabelPicker
                      value={labelIds}
                      labels={labels}
                      onChange={setLabelIds}
                      onCreateLabel={handleCreateLabel}
                      onDeleteLabel={handleDeleteLabel}
                      onUpdateLabel={handleUpdateLabel}
                    />
                  </CompactPropertyRow>

                  <CompactPropertyRow icon={Tags} label={t('tasks.type', 'Type')}>
                    <TaskTypePicker
                      value={taskTypeId}
                      taskTypes={taskTypes}
                      needsInvoice={needsInvoice}
                      onChange={setTaskTypeId}
                      onNeedsInvoiceChange={setNeedsInvoice}
                    />
                  </CompactPropertyRow>

                  {/* --- MORE --- */}
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground/70 px-3 pt-3 pb-1">
                    {t('tasks.more', 'More')}
                  </div>

                  <CompactPropertyRow icon={MapPin} label={t('common.location', 'Location')}>
                    <input
                      type="text"
                      value={location}
                      onChange={(e) => setLocation(e.target.value)}
                      placeholder={t('tasks.add_location', 'Add location...')}
                      className="w-full bg-transparent text-[13px] text-foreground/80 dark:text-foreground/80 placeholder:text-muted-foreground dark:placeholder:text-muted-foreground/70 focus:outline-none"
                    />
                  </CompactPropertyRow>

                  <CompactPropertyRow icon={Receipt} label={t('tasks.billable', 'Billable')}>
                    <Switch
                      checked={!!needsInvoice}
                      onCheckedChange={(v) => setNeedsInvoice(v)}
                      aria-label={t('tasks.billable', 'Billable')}
                    />
                  </CompactPropertyRow>

                  <CompactPropertyRow icon={Bell} label={t('tasks.reminders_label', 'Reminders')}>
                    <ReminderPicker value={reminders} onChange={setReminders}
                                    hasTime={!!(scheduledStart || (dueDate && dueTime))} />
                  </CompactPropertyRow>

                  <CompactPropertyRow icon={RefreshCw} label={t('tasks.repeat', 'Repeat')}>
                    <RecurrencePicker value={recurrence} onChange={setRecurrence} />
                  </CompactPropertyRow>

                  <CompactPropertyRow icon={isLocked ? Lock : LockOpen} label={t('tasks.lock', 'Lock')}>
                    <Switch
                      checked={isLocked}
                      onCheckedChange={(v) => setIsLocked(v)}
                      aria-label={isLocked ? t('tasks.unlock_description') : t('tasks.lock_description')}
                    />
                  </CompactPropertyRow>

                  {/* Attachment upload in right panel */}
                  <CompactPropertyRow icon={Paperclip} label={t('tasks.attachments', 'Attachments')}>
                    <AttachmentUploadButton
                      onChange={handleFileSelect}
                      uploading={uploadingFile}
                      count={attachments.length}
                    />
                  </CompactPropertyRow>

                  {/* Delete button in edit mode */}
                  {editingTask && (
                    <div className="px-3 pt-2 pb-3">
                      <button
                        onClick={handleMoveToTrash}
                        className="flex items-center gap-2 px-3 py-2 w-full rounded-lg text-sm text-red-400 hover:bg-red-500/10 transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                        <span>{t('tasks.move_to_trash')}</span>
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* ============ ERROR ============ */}
              {error && (
                <div className="mx-4 my-2 p-3 bg-red-500/10 border border-red-500/20 rounded-lg flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 text-red-400 flex-shrink-0" />
                  <span className="text-red-400 text-sm">{error}</span>
                </div>
              )}

              {/* ============ FOOTER ============ */}
              <div className="flex items-center justify-between px-5 py-3 border-t border-border dark:border-border bg-muted dark:bg-white/[0.02] flex-shrink-0">
                <div className="hidden md:flex items-center gap-1.5 text-[11px] text-muted-foreground/70">
                  <kbd className="px-1.5 py-0.5 bg-muted dark:bg-muted rounded text-muted-foreground border border-border dark:border-border">
                    {navigator.platform?.includes('Mac') ? '⌘' : 'Ctrl'}+Enter
                  </kbd>
                  <span>{t('tasks.to_save', 'to save')}</span>
                </div>

                <div className="flex items-center gap-2 max-md:flex-1 max-md:justify-between">
                  <button
                    onClick={handleClose}
                    className="px-4 py-2 text-sm text-muted-foreground dark:text-muted-foreground hover:text-foreground dark:hover:text-foreground transition-colors rounded-lg hover:bg-muted dark:hover:bg-white/5"
                  >
                    {t('common.cancel')}
                  </button>
                  <button
                    onClick={handleSubmit}
                    disabled={!title.trim() || saving}
                    className="px-5 py-2 bg-amber-500 text-black rounded-lg font-medium text-sm hover:bg-amber-400 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 transition-colors"
                  >
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                    {editingTask ? t('common.save') : t('tasks.add_task_button')}
                  </button>
                </div>
              </div>
            </div>
          </DialogPrimitive.Content>
        </DialogPrimitive.Portal>
      </DialogPrimitive.Root>

      {/* Modals on top */}
      <AttachmentPreviewModal
        attachment={previewAttachment}
        attachments={attachments}
        onClose={() => setPreviewAttachment(null)}
        onNavigate={(att) => setPreviewAttachment(att)}
      />
      <SeriesUpdateDialog
        isOpen={!!seriesDialogPayload}
        onJustThis={handleSeriesJustThis}
        onAllFuture={handleSeriesAllFuture}
        onCancel={() => { setSeriesDialogPayload(null); setSeriesPendingMeta(null); }}
      />
    </>
  );
};

export default UnifiedTaskCreation;
